import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PPA_HOME_ROUTE } from 'config/urls';
import { trackEventMixpanel } from 'utils/mixpanel';
import { useWhitespaceAnalysisContext } from 'contexts/WhitespaceAnalysisContext';
import { PPA_WHITESPACE_FILTER_APPLIED } from 'utils/mixpanelEventNames';
import DownloadStepperModal from 'components/molecules/common/DownloadStepperModal';
import GenericWhitespaceGrid from 'components/organism/WhitespaceAnalysis/GenericWhitespaceGrid';
import useWhitespaceCardPagination from 'hooks/useWhitespaceCardPagination';

import Loader from 'components/molecules/Loader';
import useWhitespaceAxesControls from './WhitespaceAnalysis/useWhitespaceAxesControls';
import useWhitespaceCellRenderer from './WhitespaceAnalysis/useWhitespaceCellRenderer';
import useWhitespaceMixpanel from './WhitespaceAnalysis/useWhitespaceMixpanel';
import useWhitespaceIncrement from './WhitespaceAnalysis/useWhitespaceIncrement';
import { computeWhitespaceGridMetrics } from './WhitespaceAnalysis/whitespaceMixpanel.helpers';
import WhitespaceAnalysisToolbar from './WhitespaceAnalysis/WhitespaceAnalysisToolbar';
import { WhitespaceAnalysisBreadcrumb, WhitespaceAnalysisPageHeader } from './WhitespaceAnalysisHeader';
import useWhitespaceExport, { WHITESPACE_DOWNLOAD_STEP_ONE_OPTIONS, WHITESPACE_DOWNLOAD_STEP_TWO_OPTIONS } from './WhitespaceAnalysis/useWhitespaceExport';

const WhitespaceAnalysis = () => {
    const navigate = useNavigate();
    const {
        title,
        tooltip,
        xAxis,
        setXAxis,
        yAxis,
        setYAxis,
        selectedYAxisKpiId,
        crossSection,
        setCrossSection,
        incrementValue,
        setIncrementValue,
        showWhitespaces,
        setShowWhitespaces,
        incrementConfig,
        xAxisOptions,
        yAxisOptions,
        crossSectionOptions,
        xAxisFilter,
        childLimit,
        gridRequestPayload,
        columnHeaders,
        gridRows,
        isGridFetched,
        loadMoreRows,
        loadMoreColumns,
        hasMoreRows,
        hasMoreColumns,
        isFetchingMoreRows,
        isFetchingMoreColumns,
        isMetaLoading,
        isGridFetching,
    } = useWhitespaceAnalysisContext();

    const gridMetrics = useMemo(() => computeWhitespaceGridMetrics(gridRows, columnHeaders), [columnHeaders, gridRows] );

    const {
        baseProps,
        comparisonMode,
        markConfigRefreshStart,
        trackAxesMenuOpened,
        trackXAxisSelected,
        trackYAxisSelected,
        trackCrossSectionOpened,
        trackCrossSectionSelected,
    } = useWhitespaceMixpanel({
        isMetaLoading,
        isGridFetched,
        isGridFetching,
        xAxis,
        yAxis,
        crossSection,
        incrementValue,
        showWhitespaces,
        xAxisOptions,
        yAxisOptions,
        crossSectionOptions,
        columnHeaders,
        gridRows,
        selectedYAxisKpiId,
    });

    const getGridMetrics = useCallback(
        () => ({
            total_cell_count: gridMetrics.total_cell_count,
            opportunity_cell_count: gridMetrics.opportunity_cell_count,
        }),
        [gridMetrics.opportunity_cell_count, gridMetrics.total_cell_count],
    );

    const handleFilterApplied = useCallback(
        (payload: {
            filter_name: string;
            previous_value: string;
            selected_value: string;
            total_cell_count_before: number;
            total_cell_count_after: number;
            opportunity_cell_count_after: number;
        }) => {
            trackEventMixpanel(PPA_WHITESPACE_FILTER_APPLIED, {
                ...payload,
                ...baseProps,
            });
        },
        [baseProps],
    );

    const { getCellPaginationData, loadMoreForCell, collapseCellItems } = useWhitespaceCardPagination({
        gridRequestPayload,
        xAxisFilter,
        childLimit,
        onFilterApplied: handleFilterApplied,
        getGridMetrics,
    });

    const {
        isDownloadModalOpen,
        handleOpenDownloadModal,
        handleCloseDownloadModal,
        handleExportScopeChange,
        handleDownloadExport,
    } = useWhitespaceExport({
        xAxis,
        yAxis,
        crossSection,
        incrementValue,
        showWhitespaces,
        comparisonMode,
        gridMetrics,
        columnHeaders,
        gridRows,
        selectedYAxisKpiId,
        getCellPaginationData,
    });

    const axesControls = useWhitespaceAxesControls({
        xAxis,
        yAxis,
        crossSection,
        setXAxis,
        setYAxis,
        setCrossSection,
        markConfigRefreshStart,
        trackAxesMenuOpened,
        trackXAxisSelected,
        trackYAxisSelected,
        trackCrossSectionOpened,
        trackCrossSectionSelected,
    });

    const {
        showDollarPrefix,
        localIncrementValue,
        handleIncrementChange,
        handleIncrementInputChange,
        handleIncrementInputParser,
        handleShowWhitespacesChange,
    } = useWhitespaceIncrement({
        yAxis,
        incrementValue,
        incrementConfig,
        setIncrementValue,
        showWhitespaces,
        setShowWhitespaces,
        baseProps,
        gridMetrics,
        markConfigRefreshStart,
    });

    const renderWhitespaceCell = useWhitespaceCellRenderer({ yAxis, crossSection, showWhitespaces, selectedYAxisKpiId, getCellPaginationData, collapseCellItems, loadMoreForCell });

    const handleBack = useCallback(() => navigate(PPA_HOME_ROUTE), [navigate]);

    if (isMetaLoading) {
        return (
            <div className="w-full h-[925px] bg-white rounded-lg">
                <Loader size="default" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 py-2">
            <WhitespaceAnalysisBreadcrumb title={title} />
            <div className="rounded-xl bg-white p-4">
                <WhitespaceAnalysisPageHeader
                    title={title}
                    onBack={handleBack}
                    tooltipContent={tooltip}
                    onDownloadClick={handleOpenDownloadModal}
                />
                <WhitespaceAnalysisToolbar
                    xAxisOptions={xAxisOptions}
                    yAxisOptions={yAxisOptions}
                    crossSection={crossSection}
                    crossSectionOptions={crossSectionOptions}
                    incrementConfig={incrementConfig}
                    showWhitespaces={showWhitespaces}
                    pendingXAxis={axesControls.pendingXAxis}
                    pendingYAxis={axesControls.pendingYAxis}
                    isAxesPopoverOpen={axesControls.isAxesPopoverOpen}
                    isCrossSectionOpen={axesControls.isCrossSectionOpen}
                    showDollarPrefix={showDollarPrefix}
                    localIncrementValue={localIncrementValue}
                    onAxesPopoverOpenChange={axesControls.handleAxesPopoverOpenChange}
                    onPendingXAxisChange={axesControls.handlePendingXAxisChange}
                    onPendingYAxisChange={axesControls.handlePendingYAxisChange}
                    onApplyAxes={axesControls.handleApplyAxes}
                    onCrossSectionOpenChange={axesControls.handleCrossSectionOpenChange}
                    onCrossSectionSelect={axesControls.handleCrossSectionSelect}
                    onIncrementChange={handleIncrementChange}
                    onIncrementInputChange={handleIncrementInputChange}
                    onIncrementInputParser={handleIncrementInputParser}
                    onShowWhitespacesChange={handleShowWhitespacesChange}
                />
                <div className="mt-4">
                    <GenericWhitespaceGrid
                        isFetched={isGridFetched}
                        columnHeaders={columnHeaders}
                        gridRows={gridRows}
                        renderCell={renderWhitespaceCell}
                        onLoadMoreRows={loadMoreRows}
                        onLoadMoreColumns={loadMoreColumns}
                        hasMoreRows={hasMoreRows}
                        hasMoreColumns={hasMoreColumns}
                        isLoadingMoreRows={isFetchingMoreRows}
                        isLoadingMoreColumns={isFetchingMoreColumns}
                        isGridFetching={isGridFetching}
                    />
                </div>
            </div>
            <DownloadStepperModal
                open={isDownloadModalOpen}
                onClose={handleCloseDownloadModal}
                onExport={handleDownloadExport}
                stepOneOptions={WHITESPACE_DOWNLOAD_STEP_ONE_OPTIONS}
                stepTwoOptions={WHITESPACE_DOWNLOAD_STEP_TWO_OPTIONS}
                onStepOneChange={handleExportScopeChange}
            />
        </div>
    );
};

export default WhitespaceAnalysis;
